import { useState, useEffect, useRef, useMemo } from "react";
import { useHotkeys } from "react-hotkeys-hook";
import toast from "react-hot-toast";
import { HiBadgeCheck, HiExclamation, HiOutlineSearch, HiOutlineShieldCheck } from "react-icons/hi";
import { LuArrowRight, LuTriangleAlert, LuUserPlus } from "react-icons/lu";
import { PiLockSimpleBold } from "react-icons/pi";
import { useFloating, autoUpdate, offset, flip, shift, size, useDismiss, useRole, useInteractions, FloatingPortal } from "@floating-ui/react";
import { useQuery, keepPreviousData } from "@tanstack/react-query";

import ConflictChoice from "./ConflictChoice";
import TextInput from "./TextInput";
import SelectInput from "./SelectInput";
import API from "../../api";
import { capture } from "../../instrument";
import { FIELD_STATE, isRequiredFieldState, TERMINAL_ADMISSION_STATUSES, formatDayMonthYear, normalizeDate, normalizeText, sameFieldValue } from "../../utils";
import { INSEE_COUNTRIES } from "../../utils/inseeCountries";

const ADDRESS_MIN_QUERY_LENGTH = 3;
const BIRTHPLACE_MIN_QUERY_LENGTH = 2;
const ADDRESS_DEBOUNCE_MS = 300;
const ADDRESS_STALE_TIME_MS = 60_000;
const ADDRESS_POPOVER_Z_INDEX = 60;

export const LabeledCell = ({ label, children, attention = false, warning, locked = false, className = "", dataFocusField }) => (
  <div className={`min-w-0 ${className}`} data-focus-field={dataFocusField}>
    <div className={`mb-1 flex items-center gap-1 pl-[9px] text-[10px] font-medium uppercase leading-none tracking-[0.08em] ${attention ? "text-orange-600" : "text-gray-500"}`}>
      <span>{label}</span>
      {warning && (
        <span title={warning} className="-my-0.5 flex">
          <LuTriangleAlert className="h-3 w-3 text-orange-500" />
        </span>
      )}
    </div>
    <div className="min-w-0 text-[13px] leading-snug text-gray-900">
      {locked ? (
        <fieldset disabled className="min-w-0">
          {children}
        </fieldset>
      ) : (
        children
      )}
    </div>
  </div>
);

const fetchAddressSuggestions = async (query, type, { signal } = {}) => {
  const res = await fetch(`https://data.geopf.fr/geocodage/search/?q=${encodeURI(query)}&limit=15&type=${type}`, { signal });
  const data = await res.json();
  return data?.features || [];
};

const INSEE_CODE_REGEX = /^(\d{2}|2[ab])\d{3}$/i;
const UNKNOWN_BIRTHPLACE_CODE = "99999";
const UNKNOWN_BIRTHPLACE_LABEL = "Inconnu";
const FRANCE_COUNTRY_CODE = "99100";
const FOREIGN_COUNTRIES = INSEE_COUNTRIES.filter((item) => item.code !== FRANCE_COUNTRY_CODE);

const toBirthplaceSuggestion = (code, label) => ({ properties: { id: code, label, citycode: code } });

const normalizeCountryName = (value) => normalizeText(value.replace(/[-'’]/g, " "));

const UNKNOWN_BIRTHPLACE_QUERIES = ["99", UNKNOWN_BIRTHPLACE_CODE, normalizeText(UNKNOWN_BIRTHPLACE_LABEL)];

const unknownBirthplaceSuggestions = (query) =>
  UNKNOWN_BIRTHPLACE_QUERIES.includes(normalizeText(query)) ? [toBirthplaceSuggestion(UNKNOWN_BIRTHPLACE_CODE, UNKNOWN_BIRTHPLACE_LABEL)] : [];

const fetchBirthplaceSuggestions = async (query, { signal } = {}) => {
  if (INSEE_CODE_REGEX.test(query)) {
    const code = query.toUpperCase();
    if (code.startsWith("99")) {
      const country = FOREIGN_COUNTRIES.find((item) => item.code === code);
      return country ? [toBirthplaceSuggestion(country.code, country.label)] : unknownBirthplaceSuggestions(query);
    }
    const res = await fetch(`https://geo.api.gouv.fr/communes/${code}?fields=nom`, { signal });
    if (res.ok === true) {
      const commune = await res.json();
      return [toBirthplaceSuggestion(code, commune.nom)];
    }
    const postalRes = await fetch(`https://geo.api.gouv.fr/communes?codePostal=${code}&fields=nom`, { signal });
    if (postalRes.ok === false) return [];
    const communes = await postalRes.json();
    return communes.map((commune) => toBirthplaceSuggestion(commune.code, commune.nom));
  }

  const normalizedName = normalizeCountryName(query);
  const countries = FOREIGN_COUNTRIES.filter((item) => normalizeCountryName(item.label).includes(normalizedName));
  const municipalities = await fetchAddressSuggestions(query, "municipality", { signal });
  return [...unknownBirthplaceSuggestions(query), ...countries.map((item) => toBirthplaceSuggestion(item.code, item.label)), ...municipalities];
};

export const InlineAutocompleteCell = ({
  type,
  initialValue = "",
  displayValue,
  placeholder,
  title,
  onChange,
  onSelect,
  onNotFound,
  onNotFoundMessage,
  hasError = false,
  fieldState,
  disabled = false,
  className = "",
  displayClassName = "text-gray-800",
}) => {
  const [inputValue, setInputValue] = useState("");
  const [debouncedInputValue, setDebouncedInputValue] = useState("");
  const [isFocused, setIsFocused] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [userDismissed, setUserDismissed] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [isKeyboardNavigating, setIsKeyboardNavigating] = useState(false);

  const inputRef = useRef(null);
  const selectedItemRef = useRef(null);

  useEffect(() => {
    if (!isEditing) return;
    inputRef.current?.focus();
    inputRef.current?.select?.();
  }, [isEditing]);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedInputValue(inputValue), ADDRESS_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [inputValue]);

  const minQueryLength = type === "birthplace" ? BIRTHPLACE_MIN_QUERY_LENGTH : ADDRESS_MIN_QUERY_LENGTH;
  const normalizedQuery = debouncedInputValue.trim();
  const queryEnabled = normalizedQuery.length >= minQueryLength;

  const { data, isFetching, error } = useQuery({
    queryKey: ["address", type, normalizedQuery],
    queryFn: ({ signal }) => (type === "birthplace" ? fetchBirthplaceSuggestions(normalizedQuery, { signal }) : fetchAddressSuggestions(normalizedQuery, type, { signal })),
    enabled: queryEnabled,
    retry: false,
    staleTime: ADDRESS_STALE_TIME_MS,
    placeholderData: keepPreviousData,
  });

  const suggestions = queryEnabled ? data || [] : [];
  const isLoading = queryEnabled && isFetching;

  useEffect(() => {
    if (!error) return;
    if (error?.name === "AbortError") return;
    capture(error);
    toast.error("Une erreur est survenue lors de la recherche.");
  }, [error]);

  useEffect(() => {
    setSelectedIndex(0);
  }, [normalizedQuery]);

  useEffect(() => {
    if (selectedItemRef.current) selectedItemRef.current.scrollIntoView({ block: "nearest" });
  }, [selectedIndex]);

  const hasQuery = inputValue.trim().length >= minQueryLength;
  const showPopover = isFocused && hasQuery && !userDismissed;

  const { refs, floatingStyles, context } = useFloating({
    open: showPopover,
    onOpenChange: (next) => {
      if (next === false) setUserDismissed(true);
    },
    placement: "bottom-start",
    whileElementsMounted: autoUpdate,
    middleware: [
      offset(0),
      flip({ padding: 8 }),
      shift({ padding: 8 }),
      size({
        padding: 8,
        apply({ rects, availableHeight, elements }) {
          Object.assign(elements.floating.style, {
            width: `${rects.reference.width}px`,
            maxHeight: `${availableHeight}px`,
          });
        },
      }),
    ],
  });

  const dismiss = useDismiss(context);
  const role = useRole(context, { role: "dialog" });
  const { getReferenceProps, getFloatingProps } = useInteractions([dismiss, role]);

  const footerVisible = onNotFound !== null && onNotFound !== undefined && hasQuery;
  const totalItems = suggestions.length + (footerVisible ? 1 : 0);
  const footerSelected = footerVisible && selectedIndex === suggestions.length;

  const stopEditing = () => {
    setIsEditing(false);
    setInputValue("");
  };

  const selectSuggestion = (suggestion) => {
    onSelect(suggestion);
    setInputValue("");
    setIsEditing(false);
    setUserDismissed(false);
    inputRef.current?.blur();
  };

  const triggerNotFound = () => {
    onNotFound();
    setInputValue("");
    setIsEditing(false);
    setUserDismissed(false);
    inputRef.current?.blur();
  };

  const startEditing = () => {
    setInputValue(initialValue || "");
    setUserDismissed(false);
    setIsEditing(true);
  };

  const navOpts = { enabled: showPopover && totalItems > 0, enableOnFormTags: ["INPUT"], preventDefault: true };
  useHotkeys(
    "down",
    () => {
      setIsKeyboardNavigating(true);
      setSelectedIndex((i) => Math.min(totalItems - 1, i + 1));
    },
    navOpts,
    [showPopover, totalItems],
  );
  useHotkeys(
    "up",
    () => {
      setIsKeyboardNavigating(true);
      setSelectedIndex((i) => Math.max(0, i - 1));
    },
    navOpts,
    [showPopover, totalItems],
  );
  useHotkeys(
    "enter",
    () => {
      if (footerSelected === true) triggerNotFound();
      else if (suggestions[selectedIndex]) selectSuggestion(suggestions[selectedIndex]);
    },
    navOpts,
    [showPopover, totalItems, footerSelected, suggestions, selectedIndex],
  );

  const onKeyDown = (event) => {
    if (event.key === "Escape" && isEditing === true) {
      event.preventDefault();
      stopEditing();
      inputRef.current?.blur();
    }
  };

  const onPanelMouseMove = () => {
    if (isKeyboardNavigating === true) setIsKeyboardNavigating(false);
  };

  const hasValue = displayValue !== null && displayValue !== undefined && String(displayValue).trim() !== "";
  const isRequiredEmpty = isRequiredFieldState(fieldState) && !hasValue;
  const triggerLabel = placeholder || title;

  if (disabled) {
    return (
      <span
        className={`flex h-[32px] w-full cursor-not-allowed items-center gap-2 rounded-md border border-transparent bg-gray-200 px-2 text-gray-500 opacity-50 ${className}`}
        title={`${title} — verrouillé`}
        aria-disabled="true"
      >
        {hasValue ? (
          <span className={`min-w-0 flex-1 truncate text-[13px] ${displayClassName}`}>{displayValue}</span>
        ) : (
          <span className="min-w-0 flex-1 truncate text-[13px] italic text-gray-300">—</span>
        )}
        <PiLockSimpleBold className="h-3 w-3 shrink-0 text-gray-300" aria-hidden="true" />
      </span>
    );
  }

  return (
    <div className={`flex flex-col ${className}`}>
      <div
        ref={refs.setReference}
        {...getReferenceProps()}
        className={`field-shell${showPopover ? " rounded-b-none" : ""}`}
        data-state={hasError ? "error" : isRequiredEmpty ? "required" : undefined}
      >
        <input
          ref={inputRef}
          type="text"
          value={isEditing ? inputValue : hasValue ? displayValue : ""}
          onChange={(event) => {
            setInputValue(event.target.value);
            setUserDismissed(false);
            onChange?.(event.target.value);
          }}
          onKeyDown={onKeyDown}
          onFocus={() => {
            setIsFocused(true);
            if (isEditing === false) startEditing();
          }}
          onBlur={() =>
            setTimeout(() => {
              setIsFocused(false);
              if (isEditing === true) stopEditing();
            }, 150)
          }
          placeholder={triggerLabel}
          aria-label={title}
          className={`min-w-0 flex-1 bg-transparent text-sm outline-none ${displayClassName} ${isRequiredEmpty ? "placeholder:text-orange-400" : "placeholder:text-gray-400"}`}
        />
        {isFocused && (isLoading ? <div className="field-spinner" /> : <HiOutlineSearch className="size-4 shrink-0 text-gray-400" />)}
      </div>

      {showPopover && (
        <FloatingPortal>
          <div ref={refs.setFloating} style={{ ...floatingStyles, zIndex: ADDRESS_POPOVER_Z_INDEX }} className="flex flex-col" {...getFloatingProps()}>
            <div className="suggestion-panel" data-modal="true" onMouseMove={onPanelMouseMove}>
              {suggestions.length > 0 && (
                <ul role="list" className="min-h-0 flex-1 overflow-auto py-1">
                  {suggestions.map((suggestion, suggestionIndex) => {
                    const isSelected = suggestionIndex === selectedIndex;
                    return (
                      <li
                        key={suggestion.properties?.id || suggestion.properties?.label || suggestionIndex}
                        ref={isSelected ? selectedItemRef : null}
                        onMouseDown={(event) => {
                          event.preventDefault();
                          selectSuggestion(suggestion);
                        }}
                        onMouseEnter={() => {
                          if (isKeyboardNavigating === false) setSelectedIndex(suggestionIndex);
                        }}
                        className="suggestion-item"
                        data-selected={isSelected}
                      >
                        <div className={`truncate px-3 py-1.5 text-[13px] leading-snug ${isSelected ? "text-primary-700" : "text-gray-700"}`} title={suggestion.properties.label}>
                          {suggestion.properties.label}
                        </div>
                      </li>
                    );
                  })}
                </ul>
              )}

              {hasQuery && isLoading && suggestions.length === 0 && <div className="suggestion-empty">Recherche…</div>}

              {hasQuery && !isLoading && suggestions.length === 0 && footerVisible === false && <div className="suggestion-empty">Aucun résultat trouvé</div>}

              {footerVisible === true && (
                <button
                  type="button"
                  onMouseDown={(event) => {
                    event.preventDefault();
                    triggerNotFound();
                  }}
                  onMouseEnter={() => {
                    if (isKeyboardNavigating === false) setSelectedIndex(suggestions.length);
                  }}
                  className="suggestion-footer"
                  data-selected={footerSelected}
                >
                  <span className="suggestion-footer-badge">+</span>
                  <span className="truncate">{onNotFoundMessage || "Saisir manuellement"}</span>
                </button>
              )}
            </div>
          </div>
        </FloatingPortal>
      )}
    </div>
  );
};

const searchPatients = async (params, signal) => {
  try {
    const { data } = await API.get(`/patient/search?${params.toString()}`, { signal });
    return data || [];
  } catch (error) {
    if (error?.name === "AbortError") return null;
    if (error?.code === "PATIENT_NOT_FOUND") return [];
    throw error;
  }
};

const OVERWRITABLE_PATIENT_FIELDS = ["firstName", "lastName", "birthLastName", "sexe", "email", "phone", "address", "zipCode", "city", "birthplace", "birthplaceCode"];

const PatientSection = ({
  admission,
  activeFields,
  validationErrors = [],
  onAdmissionChange,
  dedupMatches = [],
  setDedupMatches,
  isMerging,
  onMergeStart,
  conflicts,
  onConflictChoose,
  ProcessingStatusIcon = () => null,
}) => {
  const [isManualAddress, setIsManualAddress] = useState(false);
  const [addressLabel, setAddressLabel] = useState("");
  const [birthplaceLabel, setBirthplaceLabel] = useState("");

  const [activeSearchField, setActiveSearchField] = useState(null);
  const [patientSuggestions, setPatientSuggestions] = useState([]);
  const [selectedSuggestionIndex, setSelectedSuggestionIndex] = useState(0);
  const [isSearchingPatients, setIsSearchingPatients] = useState(false);
  const searchAbortRef = useRef(null);
  const dedupAbortRef = useRef(null);

  // One floating dropdown shared by all searchable fields; its anchor is set to whichever
  // input is focused. The combined query is built from the live patient values below.
  const { refs: suggestRefs, floatingStyles: suggestStyles } = useFloating({
    open: activeSearchField !== null,
    placement: "bottom-start",
    whileElementsMounted: autoUpdate,
    middleware: [offset(4), flip({ padding: 8 }), shift({ padding: 8 }), size({ apply: ({ elements }) => Object.assign(elements.floating.style, { width: "320px" }) })],
  });

  useEffect(() => {
    if (admission?.patient?.address) {
      const parts = [admission.patient.address, admission.patient.zipCode, admission.patient.city].filter(Boolean);
      setAddressLabel(parts.join(", "));
    }
    if (admission?.patient?.birthplace) {
      setBirthplaceLabel(admission.patient.birthplace);
    }
  }, [admission?._id, admission?.patient?.address, admission?.patient?.zipCode, admission?.patient?.city, admission?.patient?.birthplace]);

  const patient = admission?.patient;
  const flags = admission?.flags || [];
  const isLinkedToExternalPatient = !!patient?.silPatientNumber;
  const isReadOnly = TERMINAL_ADMISSION_STATUSES.includes(admission?.status);
  const patientSearchEnabled = !isLinkedToExternalPatient && !isReadOnly;

  const lockedFields = useMemo(() => {
    if (!isLinkedToExternalPatient || isReadOnly) return {};
    const filled = (value) => String(value ?? "").trim() !== "";
    return {
      firstName: filled(patient?.firstName),
      lastName: filled(patient?.lastName),
      birthLastName: filled(patient?.birthLastName),
      sexe: filled(patient?.sexe),
      birthdate: filled(patient?.birthdate),
      birthplace: filled(patient?.birthplace),
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [admission?._id, isLinkedToExternalPatient, isReadOnly]);

  const searchFirstName = (patient?.firstName || "").trim();
  const searchLastName = (patient?.lastName || "").trim();
  const searchBirthLastName = (patient?.birthLastName || "").trim();
  const searchBirthdate = normalizeDate(patient?.birthdate);
  const searchEmail = (patient?.email || "").trim();
  const searchPhone = (patient?.phone || "").trim();
  const searchINS = (patient?.INS?.value || "").trim();

  useEffect(() => {
    if (!patientSearchEnabled || !activeSearchField) {
      setPatientSuggestions([]);
      return;
    }

    const params = new URLSearchParams({ mode: "autocomplete" });
    if (searchFirstName.length >= 2) params.set("firstName", searchFirstName);
    if (searchLastName.length >= 2) params.set("lastName", searchLastName);
    if (searchBirthLastName.length >= 2) params.set("birthLastName", searchBirthLastName);
    if (searchBirthdate.length >= 2) params.set("birthdate", searchBirthdate);
    if (searchEmail.length >= 2) params.set("email", searchEmail);
    if (searchPhone.length >= 2) params.set("phone", searchPhone);
    if (searchINS.length === 15) params.set("INS", searchINS);

    const filledFieldCount = Array.from(params.keys()).filter((key) => key !== "mode").length;
    if (filledFieldCount === 0) {
      setPatientSuggestions([]);
      return;
    }

    const timer = setTimeout(async () => {
      try {
        setIsSearchingPatients(true);
        if (searchAbortRef.current) searchAbortRef.current.abort();
        searchAbortRef.current = new AbortController();
        const { signal } = searchAbortRef.current;

        const { data } = await API.get(`/patient/search?${params.toString()}`, { signal });
        if (signal.aborted) return;

        setPatientSuggestions(data || []);
        setSelectedSuggestionIndex(0);
      } catch (error) {
        if (error?.name === "AbortError") return;
        if (error?.code === "PATIENT_NOT_FOUND") return setPatientSuggestions([]);
        capture(error);
      } finally {
        setIsSearchingPatients(false);
      }
    }, 120);

    return () => {
      clearTimeout(timer);
      if (searchAbortRef.current) searchAbortRef.current.abort();
    };
  }, [activeSearchField, patientSearchEnabled, searchFirstName, searchLastName, searchBirthLastName, searchBirthdate, searchEmail, searchPhone, searchINS]);

  useEffect(() => {
    if (!patientSearchEnabled) {
      setDedupMatches([]);
      return;
    }

    const canQueryByINS = searchINS.length === 15 && searchBirthdate.length === 10;
    const canQueryByNameBirth = searchBirthdate.length === 10 && searchFirstName.length >= 3 && searchLastName.length >= 3;
    const canQueryByBirthNameBirth = searchBirthdate.length === 10 && searchFirstName.length >= 3 && searchBirthLastName.length >= 3;

    if (!canQueryByINS && !canQueryByNameBirth && !canQueryByBirthNameBirth) {
      setDedupMatches([]);
      return;
    }

    const timer = setTimeout(async () => {
      try {
        if (dedupAbortRef.current) dedupAbortRef.current.abort();
        dedupAbortRef.current = new AbortController();
        const { signal } = dedupAbortRef.current;

        const requests = [];
        if (canQueryByINS) {
          requests.push(searchPatients(new URLSearchParams({ INS: searchINS, birthdate: searchBirthdate }), signal));
        }
        if (canQueryByNameBirth) {
          requests.push(
            searchPatients(
              new URLSearchParams({
                birthdate: searchBirthdate,
                firstName: searchFirstName.slice(0, 3),
                lastName: searchLastName.slice(0, 3),
              }),
              signal,
            ),
          );
        }
        if (canQueryByBirthNameBirth) {
          requests.push(
            searchPatients(
              new URLSearchParams({
                birthdate: searchBirthdate,
                firstName: searchFirstName.slice(0, 3),
                birthLastName: searchBirthLastName.slice(0, 3),
              }),
              signal,
            ),
          );
        }

        const responses = await Promise.all(requests);
        if (signal.aborted) return;

        const allMatches = responses.filter((data) => data !== null).flat();
        const uniqueById = [...new Map(allMatches.map((p) => [String(p._id), p])).values()];
        setDedupMatches(uniqueById);
      } catch (error) {
        capture(error);
      }
    }, 300);

    return () => {
      clearTimeout(timer);
      if (dedupAbortRef.current) dedupAbortRef.current.abort();
    };
  }, [patientSearchEnabled, searchINS, searchBirthdate, searchFirstName, searchLastName, searchBirthLastName]);

  const isActive = (path) => path !== FIELD_STATE.DISABLE;

  const conflictOf = (key) => conflicts.find((conflict) => conflict.key === key);
  const isChoicePending = (key) => conflictOf(key) !== undefined && conflictOf(key).choice === undefined;
  const isLockedByMatch = (key) => isMerging && conflictOf(key) === undefined;
  const identityWarning = (key) =>
    conflictOf(key)?.isLocked === true ? `Diffère du dossier (${conflictOf(key).current || "vide"}) : la valeur de la fiche est conservée` : undefined;

  const onPatientSelect = (selectedPatient) => {
    onAdmissionChange([
      { field: "patient.firstName", value: selectedPatient.firstName || "" },
      { field: "patient.lastName", value: selectedPatient.lastName || "" },
      { field: "patient.birthLastName", value: selectedPatient.birthLastName || "" },
      { field: "patient.birthdate", value: selectedPatient.birthdate || "" },
      { field: "patient.sexe", value: selectedPatient.sexe || "" },
      { field: "patient.email", value: selectedPatient.email || "" },
      { field: "patient.phone", value: selectedPatient.phone || "" },
      { field: "patient.INS.value", value: selectedPatient.INS?.value || "" },
      { field: "patient.INS.isQualified", value: selectedPatient.INS?.isQualified === true },
      { field: "patient.address", value: selectedPatient.address || "" },
      { field: "patient.zipCode", value: selectedPatient.zipCode || "" },
      { field: "patient.city", value: selectedPatient.city || "" },
      { field: "patient.birthplace", value: selectedPatient.birthplace || "" },
      { field: "patient.birthplaceCode", value: selectedPatient.birthplaceCode || "" },
      { field: "patient.silPatientNumber", value: selectedPatient.silPatientNumber || "" },
      { field: "patient.patientId", value: selectedPatient.patientId || "" },
      { field: "patient.mutuelle.code", value: selectedPatient.mutuelle?.code || "" },
      { field: "patient.mutuelle.name", value: selectedPatient.mutuelle?.name || "" },
      { field: "patient.mutuelle.adherentNumber", value: selectedPatient.mutuelle?.adherentNumber || "" },
      { field: "patient.mutuelle.rightsFrom", value: selectedPatient.mutuelle?.rightsFrom || "" },
      { field: "patient.mutuelle.rightsTo", value: selectedPatient.mutuelle?.rightsTo || "" },
    ]);

    const parts = [selectedPatient.address, selectedPatient.zipCode, selectedPatient.city].filter(Boolean);
    setAddressLabel(parts.join(", "));
    setBirthplaceLabel(selectedPatient.birthplace || "");
    setIsManualAddress(false);

    setActiveSearchField(null);
    setPatientSuggestions([]);
    setSelectedSuggestionIndex(0);
  };

  const nonOverwritingSuggestions = useMemo(
    () =>
      patientSuggestions.filter(
        (suggestion) =>
          (!searchINS || suggestion.INS?.value === searchINS) &&
          (!searchBirthdate || normalizeDate(suggestion.birthdate) === searchBirthdate) &&
          OVERWRITABLE_PATIENT_FIELDS.every((field) => !patient?.[field] || sameFieldValue(patient[field], suggestion[field])),
      ),
    [patientSuggestions, patient, searchINS, searchBirthdate],
  );

  const searchNavOpts = { enabled: activeSearchField !== null && nonOverwritingSuggestions.length > 0, enableOnFormTags: ["INPUT"], preventDefault: true };
  useHotkeys("down", () => setSelectedSuggestionIndex((i) => Math.min(i + 1, nonOverwritingSuggestions.length - 1)), searchNavOpts, [activeSearchField, nonOverwritingSuggestions]);
  useHotkeys("up", () => setSelectedSuggestionIndex((i) => Math.max(i - 1, 0)), searchNavOpts, [activeSearchField, nonOverwritingSuggestions]);
  useHotkeys(
    "enter",
    () => {
      const choice = nonOverwritingSuggestions[selectedSuggestionIndex] || nonOverwritingSuggestions[0];
      if (choice) onPatientSelect(choice);
    },
    searchNavOpts,
    [activeSearchField, nonOverwritingSuggestions, selectedSuggestionIndex],
  );

  const onField = (fieldPath) => (event) => onAdmissionChange([{ field: fieldPath, value: event.target.value }]);

  // Searchable fields drive the shared suggestions dropdown: focus sets the anchor + active field,
  // blur clears it (suggestion clicks use onMouseDown preventDefault so they fire before blur).
  const searchableProps = (fieldKey) => {
    if (!patientSearchEnabled) return {};
    return {
      onFocus: (event) => {
        suggestRefs.setReference(event.target);
        setActiveSearchField(fieldKey);
        setSelectedSuggestionIndex(0);
      },
      onBlur: () => setActiveSearchField((current) => (current === fieldKey ? null : current)),
    };
  };

  const identityProofVerified = patient?.identityProofVerified === true;
  const age = patient?.birthdate ? Math.floor((Date.now() - new Date(patient.birthdate).getTime()) / (1000 * 60 * 60 * 24 * 365.25)) : null;
  const birthInput = searchBirthdate;
  const addressFull = [patient?.address, patient?.zipCode, patient?.city].filter(Boolean).join(", ");
  const sexeOptions = [
    { value: "M", label: "Homme" },
    { value: "F", label: "Femme" },
  ];
  const contactShown = isActive(activeFields?.patient?.phone) || isActive(activeFields?.patient?.email) || isActive(activeFields?.patient?.address);
  const morphoShown = isActive(activeFields?.patient?.weight) || isActive(activeFields?.patient?.height);
  const nameMissing = flags.includes("PATIENT_NAME_MISSING_IN_PRESCRIPTION");

  const isMissing = (state, filled) => !isReadOnly && !isMerging && isRequiredFieldState(state) && !filled;

  return (
    <>
      <div className="mb-2 flex items-center justify-between gap-3">
        <div className="flex min-w-0 items-center gap-2">
          <div className="section-title">
            <span>Patient</span>
            <ProcessingStatusIcon type="PATIENT" />
          </div>
          {age !== null && (
            <span className="inline-flex items-center rounded-full bg-gray-50 px-2 py-0.5 text-[10px] font-medium text-gray-600 ring-1 ring-inset ring-gray-100">{age} ans</span>
          )}
          {patient?.silPatientNumber && (
            <span
              className="group inline-flex items-center gap-1 rounded-full bg-gray-50 px-2 py-0.5 font-mono text-[10px] text-gray-600 ring-1 ring-inset ring-gray-100"
              title="Patient lié à un dossier existant — identité synchronisée et verrouillée"
            >
              {patient.silPatientNumber}
              <button
                type="button"
                onClick={() => onAdmissionChange([{ field: "patient.silPatientNumber", value: "" }])}
                className="-mr-0.5 flex size-3 items-center justify-center rounded-full text-gray-400 hover:bg-gray-200 hover:text-gray-700"
                title="Délier le dossier (déverrouille les champs)"
              >
                <span className="text-xs leading-none">×</span>
              </button>
            </span>
          )}
        </div>
        <div className="flex items-center gap-x-2">
          {nameMissing && (
            <span className="chip border-red-200 bg-red-50 text-red-700">
              <HiExclamation className="h-3.5 w-3.5 text-red-500" />
              Nom/prénom différents sur l'ordonnance
            </span>
          )}
          {dedupMatches.length > 0 && !isMerging && (
            <button
              type="button"
              onClick={onMergeStart}
              className="flex items-center gap-1.5 rounded-full border border-orange-200 bg-orange-50 py-0.5 pl-2.5 pr-2 text-xs font-medium text-orange-700 transition-colors hover:border-orange-300 hover:bg-orange-100"
            >
              <LuUserPlus className="h-3.5 w-3.5 shrink-0" />
              Ce patient existe déjà · {dedupMatches.length} correspondance{dedupMatches.length > 1 ? "s" : ""}
              <span className="ml-1 flex items-center gap-0.5 border-l border-orange-200 pl-2 font-semibold">
                Fusionner
                <LuArrowRight className="h-3 w-3" />
              </span>
            </button>
          )}
        </div>
      </div>

      <div className={`card relative overflow-hidden transition-shadow ${isMerging ? "shadow-[0_0_0_1px_rgba(16,24,40,0.06),0_20px_48px_-16px_rgba(16,24,40,0.28)]" : ""}`}>
        <div className="p-4">
          <div className={`grid grid-cols-2 gap-x-5 gap-y-4 sm:grid-cols-3 ${isMerging ? "" : "lg:grid-cols-4"}`}>
            {isActive(activeFields?.patient?.firstName) && (
              <LabeledCell
                label="Prénom"
                warning={identityWarning("firstName")}
                dataFocusField="patient.firstName"
                attention={isChoicePending("firstName") || isMissing(activeFields?.patient?.firstName, !!searchFirstName)}
                locked={isLockedByMatch("firstName")}
              >
                {conflictOf("firstName") ? (
                  <ConflictChoice conflict={conflictOf("firstName")} onChoose={onConflictChoose} />
                ) : (
                  <TextInput
                    fieldState={activeFields?.patient?.firstName}
                    value={patient?.firstName}
                    onChange={onField("patient.firstName")}
                    placeholder="—"
                    disabled={lockedFields.firstName}
                    {...searchableProps("firstName")}
                  />
                )}
              </LabeledCell>
            )}
            {isActive(activeFields?.patient?.lastName) && (
              <LabeledCell
                label="Nom"
                dataFocusField="patient.lastName"
                attention={isChoicePending("lastName") || isMissing(activeFields?.patient?.lastName, !!searchLastName)}
                locked={isLockedByMatch("lastName")}
              >
                {conflictOf("lastName") ? (
                  <ConflictChoice conflict={conflictOf("lastName")} onChoose={onConflictChoose} />
                ) : (
                  <TextInput
                    fieldState={activeFields?.patient?.lastName}
                    value={patient?.lastName}
                    onChange={onField("patient.lastName")}
                    placeholder="—"
                    disabled={lockedFields.lastName}
                    {...searchableProps("lastName")}
                  />
                )}
              </LabeledCell>
            )}
            {isActive(activeFields?.patient?.birthLastName) && (
              <LabeledCell
                label="Nom de naissance"
                warning={identityWarning("birthLastName")}
                dataFocusField="patient.birthLastName"
                attention={isChoicePending("birthLastName") || isMissing(activeFields?.patient?.birthLastName, !!searchBirthLastName)}
                locked={isLockedByMatch("birthLastName")}
              >
                {conflictOf("birthLastName") ? (
                  <ConflictChoice conflict={conflictOf("birthLastName")} onChoose={onConflictChoose} />
                ) : (
                  <TextInput
                    fieldState={activeFields?.patient?.birthLastName}
                    value={patient?.birthLastName}
                    onChange={onField("patient.birthLastName")}
                    placeholder="—"
                    disabled={lockedFields.birthLastName}
                    {...searchableProps("birthLastName")}
                  />
                )}
              </LabeledCell>
            )}
          </div>

          <div className={`mt-4 grid grid-cols-2 gap-x-5 gap-y-4 sm:grid-cols-3 ${isMerging ? "" : "lg:grid-cols-4"}`}>
            {isActive(activeFields?.patient?.sexe) && (
              <LabeledCell
                label="Sexe"
                warning={identityWarning("sexe")}
                dataFocusField="patient.sexe"
                attention={isChoicePending("sexe") || isMissing(activeFields?.patient?.sexe, !!patient?.sexe)}
                locked={isLockedByMatch("sexe")}
              >
                {conflictOf("sexe") ? (
                  <ConflictChoice conflict={conflictOf("sexe")} onChoose={onConflictChoose} />
                ) : (
                  <SelectInput
                    fieldState={activeFields?.patient?.sexe}
                    value={patient?.sexe}
                    onChange={onField("patient.sexe")}
                    options={sexeOptions}
                    disabled={lockedFields.sexe}
                  />
                )}
              </LabeledCell>
            )}
            {isActive(activeFields?.patient?.birthdate) && (
              <LabeledCell
                label="Né(e) le"
                warning={identityWarning("birthdate")}
                dataFocusField="patient.birthdate"
                attention={isChoicePending("birthdate") || isMissing(activeFields?.patient?.birthdate, !!searchBirthdate)}
                locked={isLockedByMatch("birthdate")}
              >
                {conflictOf("birthdate") ? (
                  <ConflictChoice conflict={conflictOf("birthdate")} onChoose={onConflictChoose} />
                ) : (
                  <TextInput
                    type="date"
                    fieldState={activeFields?.patient?.birthdate}
                    value={birthInput}
                    onChange={onField("patient.birthdate")}
                    placeholder="jj/mm/aaaa"
                    disabled={lockedFields.birthdate}
                    {...searchableProps("birthdate")}
                  />
                )}
              </LabeledCell>
            )}
            {isActive(activeFields?.patient?.birthplaceCode) && (
              <LabeledCell
                label="Lieu naiss."
                warning={identityWarning("birthplace")}
                dataFocusField="patient.birthplaceCode"
                attention={isChoicePending("birthplace") || isMissing(activeFields?.patient?.birthplaceCode, !!patient?.birthplaceCode)}
                locked={isLockedByMatch("birthplace")}
              >
                {conflictOf("birthplace") ? (
                  <ConflictChoice conflict={conflictOf("birthplace")} onChoose={onConflictChoose} />
                ) : (
                  <InlineAutocompleteCell
                    type="birthplace"
                    initialValue={birthplaceLabel}
                    displayValue={patient?.birthplace}
                    placeholder="Code ou nom"
                    title="Lieu de naissance"
                    fieldState={activeFields?.patient?.birthplaceCode}
                    disabled={lockedFields.birthplace}
                    onChange={() =>
                      onAdmissionChange([
                        { field: "patient.birthplaceCode", value: "" },
                        { field: "patient.birthplace", value: "" },
                      ])
                    }
                    onSelect={(suggestion) => {
                      setBirthplaceLabel(suggestion.properties.label);
                      onAdmissionChange([
                        { field: "patient.birthplaceCode", value: suggestion.properties.citycode },
                        { field: "patient.birthplace", value: suggestion.properties.label },
                      ]);
                    }}
                    onNotFoundMessage="Lieu de naissance introuvable ? Cliquer ici pour continuer"
                    onNotFound={() => {
                      setBirthplaceLabel(UNKNOWN_BIRTHPLACE_LABEL);
                      onAdmissionChange([
                        { field: "patient.birthplaceCode", value: UNKNOWN_BIRTHPLACE_CODE },
                        { field: "patient.birthplace", value: UNKNOWN_BIRTHPLACE_LABEL },
                      ]);
                    }}
                  />
                )}
              </LabeledCell>
            )}
            {isActive(activeFields?.patient?.INS) && (
              <LabeledCell
                label="INS"
                dataFocusField="patient.INS.value"
                attention={isChoicePending("INS") || isMissing(activeFields?.patient?.INS, !!searchINS)}
                locked={isLockedByMatch("INS")}
              >
                {conflictOf("INS") ? (
                  <ConflictChoice conflict={conflictOf("INS")} onChoose={onConflictChoose} />
                ) : (
                  <span className="flex w-full min-w-0 items-center gap-1">
                    <TextInput
                      inRow
                      fieldState={activeFields?.patient?.INS}
                      value={patient?.INS?.value}
                      hasError={validationErrors.includes("patient.INS.value")}
                      onChange={(event) =>
                        onAdmissionChange([
                          { field: "patient.INS.value", value: event.target.value },
                          { field: "patient.INS.isQualified", value: false },
                        ])
                      }
                      placeholder="—"
                      {...searchableProps("INS.value")}
                    />
                    {patient?.INS?.isQualified === true && <HiBadgeCheck className="h-4 w-4 flex-shrink-0 text-emerald-500" />}
                  </span>
                )}
              </LabeledCell>
            )}
            {isActive(activeFields?.patient?.IPP) && (
              <LabeledCell
                label="IPP"
                dataFocusField="patient.IPP"
                attention={isChoicePending("IPP") || isMissing(activeFields?.patient?.IPP, !!patient?.IPP)}
                locked={isLockedByMatch("IPP")}
              >
                {conflictOf("IPP") ? (
                  <ConflictChoice conflict={conflictOf("IPP")} onChoose={onConflictChoose} />
                ) : (
                  <TextInput fieldState={activeFields?.patient?.IPP} value={patient?.IPP} onChange={onField("patient.IPP")} placeholder="—" />
                )}
              </LabeledCell>
            )}
            {isActive(activeFields?.patient?.admissionId) && (
              <LabeledCell
                label="Admission"
                dataFocusField="patient.admissionId"
                attention={isChoicePending("admissionId") || isMissing(activeFields?.patient?.admissionId, !!patient?.admissionId)}
                locked={isLockedByMatch("admissionId")}
              >
                {conflictOf("admissionId") ? (
                  <ConflictChoice conflict={conflictOf("admissionId")} onChoose={onConflictChoose} />
                ) : (
                  <TextInput fieldState={activeFields?.patient?.admissionId} value={patient?.admissionId} onChange={onField("patient.admissionId")} placeholder="—" />
                )}
              </LabeledCell>
            )}
            {isActive(activeFields?.patient?.hostService) && (
              <LabeledCell
                label="Service"
                dataFocusField="patient.hostService"
                attention={isChoicePending("hostService") || isMissing(activeFields?.patient?.hostService, !!patient?.hostService)}
                locked={isLockedByMatch("hostService")}
              >
                {conflictOf("hostService") ? (
                  <ConflictChoice conflict={conflictOf("hostService")} onChoose={onConflictChoose} />
                ) : (
                  <TextInput fieldState={activeFields?.patient?.hostService} value={patient?.hostService} onChange={onField("patient.hostService")} placeholder="—" />
                )}
              </LabeledCell>
            )}
            {isActive(activeFields?.patient?.room) && (
              <LabeledCell
                label="Chambre"
                dataFocusField="patient.room"
                attention={isChoicePending("room") || isMissing(activeFields?.patient?.room, !!patient?.room)}
                locked={isLockedByMatch("room")}
              >
                {conflictOf("room") ? (
                  <ConflictChoice conflict={conflictOf("room")} onChoose={onConflictChoose} />
                ) : (
                  <TextInput fieldState={activeFields?.patient?.room} value={patient?.room} onChange={onField("patient.room")} placeholder="—" />
                )}
              </LabeledCell>
            )}
          </div>

          {(contactShown || morphoShown) && (
            <div className={`mt-4 grid grid-cols-2 gap-x-5 gap-y-4 border-t border-gray-100 pt-4 sm:grid-cols-3 ${isMerging ? "" : "lg:grid-cols-4"}`}>
              {isActive(activeFields?.patient?.phone) && (
                <LabeledCell
                  label="Téléphone"
                  dataFocusField="patient.phone"
                  attention={isChoicePending("phone") || isMissing(activeFields?.patient?.phone, !!searchPhone)}
                  locked={isLockedByMatch("phone")}
                >
                  {conflictOf("phone") ? (
                    <ConflictChoice conflict={conflictOf("phone")} onChoose={onConflictChoose} />
                  ) : (
                    <TextInput
                      fieldState={activeFields?.patient?.phone}
                      value={patient?.phone}
                      hasError={validationErrors.includes("patient.phone")}
                      onChange={onField("patient.phone")}
                      placeholder="téléphone"
                      type="tel"
                      {...searchableProps("phone")}
                    />
                  )}
                </LabeledCell>
              )}
              {isActive(activeFields?.patient?.email) && (
                <LabeledCell
                  label="Email"
                  dataFocusField="patient.email"
                  attention={isChoicePending("email") || isMissing(activeFields?.patient?.email, !!searchEmail)}
                  locked={isLockedByMatch("email")}
                >
                  {conflictOf("email") ? (
                    <ConflictChoice conflict={conflictOf("email")} onChoose={onConflictChoose} />
                  ) : (
                    <TextInput
                      fieldState={activeFields?.patient?.email}
                      value={patient?.email}
                      hasError={validationErrors.includes("patient.email")}
                      onChange={onField("patient.email")}
                      placeholder="email"
                      type="email"
                      {...searchableProps("email")}
                    />
                  )}
                </LabeledCell>
              )}
              {isActive(activeFields?.patient?.address) && (
                <LabeledCell
                  label="Adresse"
                  dataFocusField="patient.address"
                  className="lg:col-span-2"
                  attention={isChoicePending("address") || isMissing(activeFields?.patient?.address, !!patient?.address)}
                  locked={isLockedByMatch("address")}
                >
                  {conflictOf("address") ? (
                    <ConflictChoice conflict={conflictOf("address")} onChoose={onConflictChoose} />
                  ) : isManualAddress ? (
                    <span className="flex flex-wrap items-center gap-2">
                      <TextInput inRow value={patient?.address} onChange={onField("patient.address")} placeholder="numéro et rue" />
                      <TextInput inRow value={patient?.zipCode} onChange={onField("patient.zipCode")} placeholder="code postal" />
                      <TextInput inRow value={patient?.city} onChange={onField("patient.city")} placeholder="ville" />
                      {!isReadOnly && (
                        <button
                          type="button"
                          onClick={() => setIsManualAddress(false)}
                          className="text-[11px] text-gray-400 underline-offset-2 hover:text-gray-600 hover:underline"
                          title="Rechercher une adresse"
                        >
                          Rechercher
                        </button>
                      )}
                    </span>
                  ) : (
                    <InlineAutocompleteCell
                      type="housenumber"
                      initialValue={addressLabel}
                      displayValue={addressFull}
                      placeholder="adresse"
                      title="Adresse"
                      displayClassName="text-gray-700"
                      hasError={validationErrors.includes("patient.address")}
                      fieldState={activeFields?.patient?.address}
                      onChange={() =>
                        onAdmissionChange([
                          { field: "patient.address", value: "" },
                          { field: "patient.zipCode", value: "" },
                          { field: "patient.city", value: "" },
                        ])
                      }
                      onSelect={(suggestion) => {
                        onAdmissionChange([
                          { field: "patient.address", value: suggestion.properties.name },
                          { field: "patient.zipCode", value: suggestion.properties.postcode },
                          { field: "patient.city", value: suggestion.properties.city },
                        ]);
                        setAddressLabel(suggestion.properties.label);
                      }}
                      onNotFoundMessage="Adresse introuvable ? Cliquer ici pour saisir manuellement"
                      onNotFound={() => setIsManualAddress(true)}
                    />
                  )}
                </LabeledCell>
              )}
              {isActive(activeFields?.patient?.weight) && (
                <LabeledCell
                  label="Poids"
                  dataFocusField="patient.weight"
                  attention={isChoicePending("weight") || isMissing(activeFields?.patient?.weight, !!patient?.weight)}
                  locked={isLockedByMatch("weight")}
                >
                  {conflictOf("weight") ? (
                    <ConflictChoice conflict={conflictOf("weight")} onChoose={onConflictChoose} />
                  ) : (
                    <TextInput fieldState={activeFields?.patient?.weight} value={patient?.weight} onChange={onField("patient.weight")} placeholder="kg" type="number" />
                  )}
                </LabeledCell>
              )}
              {isActive(activeFields?.patient?.height) && (
                <LabeledCell
                  label="Taille"
                  dataFocusField="patient.height"
                  attention={isChoicePending("height") || isMissing(activeFields?.patient?.height, !!patient?.height)}
                  locked={isLockedByMatch("height")}
                >
                  {conflictOf("height") ? (
                    <ConflictChoice conflict={conflictOf("height")} onChoose={onConflictChoose} />
                  ) : (
                    <TextInput fieldState={activeFields?.patient?.height} value={patient?.height} onChange={onField("patient.height")} placeholder="cm" type="number" />
                  )}
                </LabeledCell>
              )}
            </div>
          )}

          {!isLinkedToExternalPatient && (
            <fieldset disabled={isMerging} className="mt-4 flex items-center justify-between gap-3 border-t border-gray-100 pt-3">
              <span className="flex items-center gap-1.5 text-[13px] font-medium text-gray-700">
                <HiOutlineShieldCheck className={`h-4 w-4 ${identityProofVerified ? "text-primary-500" : "text-gray-400"}`} />
                Identité vérifiée
              </span>
              <span className="inline-flex overflow-hidden rounded-md border border-gray-300">
                <button
                  type="button"
                  onClick={() => onAdmissionChange([{ field: "patient.identityProofVerified", value: true }])}
                  className={`px-3 py-1 text-[13px] font-medium transition-colors ${identityProofVerified ? "bg-primary-500 text-white" : "bg-white text-gray-600 hover:bg-gray-50"}`}
                >
                  Oui
                </button>
                <button
                  type="button"
                  onClick={() => onAdmissionChange([{ field: "patient.identityProofVerified", value: false }])}
                  className={`border-l border-gray-300 px-3 py-1 text-[13px] font-medium transition-colors ${!identityProofVerified ? "bg-gray-100 text-gray-900" : "bg-white text-gray-600 hover:bg-gray-50"}`}
                >
                  Non
                </button>
              </span>
            </fieldset>
          )}
        </div>
      </div>

      {patientSearchEnabled && activeSearchField !== null && (nonOverwritingSuggestions.length > 0 || isSearchingPatients) && (
        <FloatingPortal>
          <div ref={suggestRefs.setFloating} style={{ ...suggestStyles, zIndex: 60 }} onMouseDown={(e) => e.preventDefault()}>
            <div className="flex max-h-72 flex-col overflow-hidden rounded-xl bg-white shadow-lg ring-1 ring-black/10">
              {nonOverwritingSuggestions.length === 0 ? (
                <div className="px-3 py-4 text-center text-[0.8125rem] italic text-gray-400">{isSearchingPatients ? "Recherche..." : "Aucun patient trouvé"}</div>
              ) : (
                <ul role="list" className="min-h-0 flex-1 overflow-auto py-1">
                  {nonOverwritingSuggestions.map((suggestion, idx) => {
                    const isSelected = selectedSuggestionIndex === idx;
                    return (
                      <li
                        key={suggestion._id || idx}
                        className="suggestion-item px-3 py-1.5"
                        data-selected={isSelected}
                        onClick={() => onPatientSelect(suggestion)}
                        onMouseEnter={() => setSelectedSuggestionIndex(idx)}
                      >
                        <div className="truncate text-[13px] font-medium leading-snug">
                          {[suggestion.lastName, suggestion.firstName].filter(Boolean).join(" ") || "Patient sans nom"}
                        </div>
                        <div className={`truncate text-[11px] leading-snug ${isSelected ? "text-primary-600/80" : "text-gray-500"}`}>
                          {[formatDayMonthYear(suggestion.birthdate), suggestion.INS?.value].filter(Boolean).join(" • ")}
                        </div>
                        {(suggestion.email || suggestion.phone) && (
                          <div className={`truncate text-[11px] leading-snug ${isSelected ? "text-primary-600/70" : "text-gray-400"}`}>
                            {[suggestion.email, suggestion.phone].filter(Boolean).join(" • ")}
                          </div>
                        )}
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
          </div>
        </FloatingPortal>
      )}
    </>
  );
};

export default PatientSection;
